/**
 * Prompt Parser Utility
 *
 * Parses user input to determine which recommendations to execute.
 *
 * Examples:
 * - "1" → [0]
 * - "1, 2" or "1 and 2" → [0, 1]
 * - "all" → [0, 1, 2, ...]
 * - "run 1 and 3" → [0, 2]
 * - Free text → null (send to Claude)
 */

export interface ParsedPrompt {
  type: 'selection' | 'query';
  indices?: number[]; // 0-based indices for selections
  query?: string; // Original text for queries
}

export function parsePrompt(input: string, totalRecommendations: number): ParsedPrompt {
  const trimmed = input.trim().toLowerCase();

  // Check for "all" command
  if (trimmed === 'all' || trimmed === 'run all' || trimmed === 'execute all') {
    return {
      type: 'selection',
      indices: Array.from({ length: totalRecommendations }, (_, i) => i),
    };
  }

  // Extract numbers from input
  const numbers = trimmed.match(/\d+/g);

  if (numbers && numbers.length > 0) {
    // Validate that input is primarily about numbers
    const cleanedInput = trimmed
      .replace(/\d+/g, '')
      .replace(/and|,|run|execute|do|&/gi, '')
      .trim();

    // If only numbers and connectors, treat as selection
    if (cleanedInput.length === 0) {
      const indices = numbers
        .map((n) => parseInt(n, 10) - 1) // Convert to 0-based
        .filter((i) => i >= 0 && i < totalRecommendations); // Validate bounds

      if (indices.length > 0) {
        return { type: 'selection', indices };
      }
    }
  }

  // Fall back to query
  return { type: 'query', query: input };
}
